import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { StripeElementChangeEvent } from '@stripe/stripe-js';

import { colors } from 'styles/theme/colors';
import { CardFieldType } from 'checkout/enums/CardFieldType';
import { isTouchDevice } from 'shared/utils/isTouchDevice';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Stripe.CreditCardInput';

interface ICreditCardInput {
  onComplete(isComplete: boolean): void;
}

export const CreditCardInput = ({ onComplete }: ICreditCardInput) => {
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(null);
  const hasError = !!error;
  const isTouch = isTouchDevice();

  const checkCompletedCount = () => {
    const completedCount = [...document.querySelectorAll('.StripeElement--complete')].length;
    onComplete(completedCount === 3);
  };

  const handleFocus = (fieldType): any => {
    setIsFocused(fieldType);
  };

  const handleBlur = () => {
    setIsFocused(null);
    checkCompletedCount();
  };

  const handleChange = (event: StripeElementChangeEvent) => {
    const { error: stripeError } = event;
    if (error) setError(stripeError.message);

    setTimeout(() => {
      checkCompletedCount();
    }, 100);
  };

  const handleResize = () => {
    /* this makes sure that we unfocus on the input when the keyboard is not present on touch devices */
    if (window.visualViewport?.height === window.innerHeight) {
      handleBlur();
    }
  };

  useEffect(() => {
    if (isTouch) {
      window.visualViewport?.addEventListener('resize', handleResize);

      return () => {
        window.visualViewport?.addEventListener('resize', handleResize);
      };
    }
  }, [isTouch]);

  return (
    <CreditCardInput.CardBox>
      <CreditCardInput.CardInput
        error={hasError}
        $isFocused={isFocused === CardFieldType.CardNumber}
        data-qa="card_number_field"
      >
        <CardNumberElement
          options={{
            placeholder: t(`${TP}.cardNumberPlaceholder`, 'Card Number'),
            style: {
              base: {
                fontSize: '11.5px',
                lineHeight: '14px',
                color: colors.black,
                textTransform: 'capitalize',
                '::placeholder': {
                  color: colors.black,
                },
                ':focus': {
                  fontSize: '13px',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
          onChange={handleChange}
          onFocus={() => handleFocus(CardFieldType.CardNumber)}
          onBlur={handleBlur}
        />
      </CreditCardInput.CardInput>
      <CreditCardInput.CardInput
        error={hasError}
        $isFocused={isFocused === CardFieldType.CardExpiry}
        data-qa="exp_field"
      >
        <CardExpiryElement
          options={{
            style: {
              base: {
                fontSize: '11.5px',
                lineHeight: '14px',
                color: colors.black,
                textTransform: 'capitalize',
                '::placeholder': {
                  color: colors.black,
                },
                ':focus': {
                  fontSize: '13px',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
          onChange={handleChange}
          onFocus={() => handleFocus(CardFieldType.CardExpiry)}
          onBlur={handleBlur}
        />
      </CreditCardInput.CardInput>
      <CreditCardInput.CardInput
        error={hasError}
        $isFocused={isFocused === CardFieldType.CVC}
        data-qa="ccv_field"
      >
        <CardCvcElement
          options={{
            placeholder: 'CVV',
            style: {
              base: {
                fontSize: '11.5px',
                lineHeight: '14px',
                color: colors.black,
                textTransform: 'capitalize',
                '::placeholder': {
                  color: colors.black,
                },
                ':focus': {
                  fontSize: '13px',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
          onChange={handleChange}
          onFocus={() => handleFocus(CardFieldType.CVC)}
          onBlur={handleBlur}
        />
      </CreditCardInput.CardInput>
    </CreditCardInput.CardBox>
  );
};

CreditCardInput.CardBox = styled.div`
  display: grid;
  grid-template-columns: 58% 20% 19%;
  grid-gap: 6px;
  margin-top: 5px;
  padding: 8px 0;
  color: ${({ theme }) => theme.colors.black};
  width: 100%;
`;

CreditCardInput.CardInput = styled.div<{ error: boolean; $isFocused: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
  width: 100%;
  padding: 4px 0 8px;
  height: 100%;

  ${({ $isFocused }) =>
    $isFocused &&
    css`
      border-bottom: 2px solid ${({ theme }) => theme.navigation.borderColor};
      margin-bottom: -1px; /* ensures the div does not shift when transitioning to 2px on focus */
    `}
`;
